import React, { useState } from "react";
import axios from "axios";

const VideoForm = () => {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" }); // Handles feedback messaging

  // Robust parsing handling desktop URLs, mobile links (youtu.be), and Shorts formats
  const convertToEmbedUrl = (inputUrl) => {
    if (!inputUrl) return "";

    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
    const match = inputUrl.match(regExp);

    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }

    return inputUrl;
  };

  const currentEmbedUrl = convertToEmbedUrl(url);
  const isVideoParsable = currentEmbedUrl.includes("youtube.com/embed/");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !url.trim()) return;

    setSubmitting(true);
    setStatus({ type: "", text: "" });

    try {
      await axios.post("https://mosque-backend-yfae.vercel.app/videos", {
        title,
        url: currentEmbedUrl,
      });

      setStatus({
        type: "success",
        text: "Vlog content added to database successfully!",
      });
      setTitle("");
      setUrl("");
    } catch (error) {
      console.error("Error saving video:", error);
      setStatus({
        type: "error",
        text: "Database connection failed. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-100 shadow-xl p-6 md:p-8">
      {/* Form Header */}
      <div className="text-center mb-8 pb-4 border-b border-slate-100">
        <h2 className="text-2xl font-serif font-bold text-slate-800 tracking-wide">
          Publish New Video Link
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Distribute and link official video lectures, updates, or vlogs to your
          media board
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title input field */}
        <div>
          <label
            htmlFor="title"
            className="block text-xs font-bold tracking-wider uppercase text-slate-600 mb-2"
          >
            Video Title
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-3 bg-stone-50 border border-slate-200 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-600/10 rounded-xl outline-none transition-all duration-200 text-slate-800"
            placeholder="e.g., Friday Khutbah - Purpose of Life"
            required
            disabled={submitting}
          />
        </div>

        {/* URL Input field */}
        <div>
          <label
            htmlFor="url"
            className="block text-xs font-bold tracking-wider uppercase text-slate-600 mb-2"
          >
            YouTube Stream Link
          </label>
          <input
            type="url"
            id="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full px-4 py-3 bg-stone-50 border border-slate-200 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-600/10 rounded-xl outline-none transition-all duration-200 text-slate-800"
            placeholder="Paste standard, mobile, or shorts URL"
            required
            disabled={submitting}
          />
        </div>

        {/* Live Video Player Preview Frame */}
        {url && (
          <div className="p-4 bg-stone-50/50 border border-slate-100 rounded-xl">
            <h3 className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-3">
              Stream Preview Channel
            </h3>
            {isVideoParsable ? (
              <div className="relative aspect-video rounded-lg overflow-hidden border border-slate-200 bg-black shadow-inner">
                <iframe
                  src={currentEmbedUrl}
                  title="YouTube video player preview"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 p-2.5 rounded-lg font-medium">
                ⚠️ Link detected but unrecognized. Please make sure it's a valid
                YouTube URL.
              </p>
            )}
          </div>
        )}

        {/* Feedback Alert Strip */}
        {status.text && (
          <div
            className={`p-4 rounded-xl text-sm font-medium border text-center transition-all ${
              status.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {status.type === "success" ? "✨ " : "❌ "}
            {status.text}
          </div>
        )}

        {/* Action Button Trigger */}
        <button
          type="submit"
          disabled={submitting || !title.trim() || !url.trim()}
          className="w-full bg-emerald-800 hover:bg-emerald-700 active:bg-emerald-900 text-white font-medium tracking-wide py-3 px-4 rounded-xl shadow-lg shadow-emerald-800/20 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Saving Content...</span>
            </>
          ) : (
            <span>Publish Video</span>
          )}
        </button>
      </form>
    </div>
  );
};

export default VideoForm;
